import React, { forwardRef, useState, useEffect, useRef } from 'react';
import SectionComment from 'src/ddvComment/SectionComment';
import AnchorComponent from '@modules/customizedModule/anchorComponent';

import { urlTabMap } from '@components/directUser/data';
import { getValueFromSearchParam } from '@utils/history';
import FullScreenButton from '@components/fullScreenButton';
import { FixedRatioSectionWrapper } from '@components/fixedRatioContainer';
import useProjectId from '@hooks/useProjectId';
import useTextContainerService from '@hooks/useTextContainerService';
import TextContainer from '@components/textContainer/TextContainer';
import { Button } from 'antd';
import './styles.scss';
import useCustomizedModuleData from '@hooks/useCustomizedModuleData';
import action from '@flux/action';
import { useDispatch, useSelector } from 'react-redux';

const FullScreenElemId = 'iframe-pdf-container';

function TextContainerModule({ history }, ref) {
  const projectId = useProjectId();
  const { integrationModuleId, moduleValue, from } = getValueFromSearchParam(
    'integrationModuleId',
    'moduleValue',
    'from',
  );
  const isFromTiles = from === 'tiles';
  const operatorName = useSelector((state) => state.login.userInfo.name);
  const dispatch = useDispatch();

  const {
    targetModuleData: { link, ratio, moduleName },
  } = useCustomizedModuleData(moduleValue, integrationModuleId, false);
  const [editable, setEditable] = useState(false);
  const textContainerRef = useRef();

  const { loading, data, saveData } = useTextContainerService(link);

  useEffect(() => {
    urlTabMap['/textContainer'] = { moduleName, identifier: moduleValue };
    return () => {
      Reflect.deleteProperty(urlTabMap, '/textContainer');
    };
  }, [moduleValue]);

  // TODO: better to create new api to update a single module
  async function updateModuleLink(newLink) {
    const projectData = await dispatch(action.getProjectSetting(projectId));
    console.log('projectData: ', projectData);
    const newData = {
      clientSetting: projectData.clientInfo,
      groupSetting: projectData.groups,
      groupSetting: projectData.groups.map((item) => {
        if (!item.groupInfo) {
          item.groupInfo = {};
        }
        item.groupInfo.projectId = projectId;
        return item;
      }),
      projectSetting: projectData.projectInfo,
      teamSetting: {
        members: [
          projectData.teamInfo.lead,
          projectData.teamInfo.manager,
          ...projectData.teamInfo.members,
        ].filter((member) => member),
        operatorName,
      },
      moduleSetting: [],
    };

    const defaultPermission = projectData.groups.map((group) => ({
      groupId: group.groupInfo.id,
      groupPermission: [],
    }));

    for (module in projectData.moduleInfo) {
      if (projectData.moduleInfo[module].value === moduleValue) {
        if (projectData.moduleInfo[module].groupPermissions) {
          newData.moduleSetting.push({
            ...projectData.moduleInfo[module],
            link: newLink,
          });
        } else {
          newData.moduleSetting.push({
            ...projectData.moduleInfo[module],
            groupPermissions: defaultPermission,
            link: newLink,
          });
        }
        continue;
      }
      if (projectData.moduleInfo[module].groupPermissions) {
        newData.moduleSetting.push(projectData.moduleInfo[module]);
      } else {
        newData.moduleSetting.push({
          ...projectData.moduleInfo[module],
          groupPermissions: defaultPermission,
        });
      }
    }

    const { success, hasAuth } = await dispatch(
      action.saveProjectSettings(newData),
    );
  }

  const handleSave = () => {
    const dataToSend = textContainerRef.current?.getData?.();
    if (dataToSend) {
      saveData({
        data: dataToSend,
        type: 'module',
        id: data?.id,
        projectId,
      })
        .then((result) => {
          setEditable(false);
          if (textContainerRef.current?.setData && result.data) {
            textContainerRef.current.setData(
              result.data?.contents,
              result.data?.columnsWidthRatio,
            );
          }
          return result?.id;
        })
        .then((textContainerId) => {
          if (!link && textContainerId) {
            return updateModuleLink(textContainerId);
          }
        })
        .then(() => {
          if (document.activeElement) {
            document.activeElement.blur();
          }
        })
        .catch(console.log);
    }
  };
  const handleEdit = () => {
    setEditable(true);
  };
  const handleCancel = () => {
    setEditable(false);
    // eslint-disable-next-line no-unused-expressions
    textContainerRef.current?.setData?.(
      data?.data?.contents || [],
      data?.data?.columnsWidthRatio || [],
    );
    if (document.activeElement) {
      document.activeElement.blur();
    }
  };

  useEffect(() => {
    if (!data.id) {
      handleCancel();
    }
  }, [data?.id]);

  return (
    <div className="page-wrapper integration-iframe-page text-container-module-page">
      <div className="integration-iframe-head">
        <span
          className="dd-back-btn"
          onClick={() => {
            history.replace(`/project-detail/${projectId}`, {
              preventRefresh: true,
              integrationModuleId,
            });
          }}
        >
          <span className="appkiticon icon-left-chevron-fill a-font-12" /> Back
        </span>
        <div className="header-right">
          <div className="button-wrapper">
            {editable ? (
              <>
                <Button
                  className="dd-cancel-btn"
                  onClick={handleCancel}
                  loading={loading}
                >
                  Cancel
                </Button>
                <Button
                  className="edit-btn"
                  type="primary"
                  onClick={handleSave}
                  loading={loading}
                >
                  Save
                </Button>
              </>
            ) : (
              <Button
                type="primary"
                className="role-control edit-btn"
                onClick={handleEdit}
                loading={loading}
              >
                Edit Text
              </Button>
            )}
          </div>
          {isFromTiles ? null : (
            <div className="customized-module-comment-icon">
              <SectionComment
                module={moduleName}
                component=""
                placement="bottomRight"
                sendIdentifier={moduleValue}
                // getPopupContainer={() =>
                //   document.getElementsByClassName('integration-tile')[0]
                // }
              />
              <AnchorComponent anchorId={moduleValue} />
            </div>
          )}
          <div style={{ width: 35 }}>
            <FullScreenButton elemId={`#${FullScreenElemId}`} />
          </div>
        </div>
      </div>
      <div
        id={FullScreenElemId}
        ratio={ratio}
        className="powerbi-fullscreen-div fix-ratio-div"
      >
        <TextContainer
          initialValues={data?.data.contents}
          initialColumnRatio={data?.data.columnsWidthRatio}
          editable={editable}
          ref={textContainerRef}
          key={moduleValue || integrationModuleId}
        />
      </div>
    </div>
  );
}

export default forwardRef(TextContainerModule);
